import { useEffect } from "react";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { z } from "zod";

import { TimedLoader } from "@/components";
import { ONBOARDING_TOKEN_STORAGE_KEY } from "@/constants";
import { LOGIN_ERRORS } from "@/domains/profile/models/constants";
import { useSessionStore } from "@/stores/use-session-store";
import { Heading } from "@/ui";

// Prevent open-redirect: accept only same-origin paths.
// "//" is the protocol-relative form (e.g. "//evil.com") and is rejected.
const safeReturnTo = (value: string | null): string => {
  const path = value ?? "/";

  return path.startsWith("/") && !path.startsWith("//") ? path : "/";
};

const loginRoute = getRouteApi("/_guest/login/");

const OidcReturn = () => {
  const navigate = loginRoute.useNavigate();
  const { error: queryError } = Route.useSearch();
  const setSession = useSessionStore((s) => {
    return s.setSession;
  });

  useEffect(() => {
    // Backend signals failure with ?error=…, success with one of two fragments:
    //   #token=…&tokenType=…&expiresIn=…&returnTo=… — returning patient, full session
    //   #onboardingToken=…&returnTo=/profiling      — first-time member, exchange next
    if (queryError) {
      sessionStorage.removeItem(ONBOARDING_TOKEN_STORAGE_KEY);
      void navigate({ to: "/login", search: { error: queryError } });

      return;
    }

    const fragment = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : "";
    const params = new URLSearchParams(fragment);
    const token = params.get("token");
    const onboardingToken = params.get("onboardingToken");
    const returnTo = safeReturnTo(params.get("returnTo"));

    if (token) {
      const tokenType = params.get("tokenType") ?? "bearer";
      const expiresIn = Number(params.get("expiresIn") ?? "0");

      // Reject malformed payloads — Number("") and Number("garbage") both
      // become NaN/0, which would persist a bogus session and 401-loop the
      // user on the first private route.
      if (!Number.isFinite(expiresIn) || expiresIn <= 0) {
        sessionStorage.removeItem(ONBOARDING_TOKEN_STORAGE_KEY);
        void navigate({ to: "/login", search: { error: LOGIN_ERRORS.INVALID_SESSION } });

        return;
      }

      // Returning-patient path: clear any stale onboarding token from a
      // previous attempt before persisting the new session.
      sessionStorage.removeItem(ONBOARDING_TOKEN_STORAGE_KEY);
      setSession({ token, type: tokenType, expiresIn });
      // Hard-navigate so the _guest layout's "already logged in → /" redirect
      // doesn't race ahead of TanStack's client-side navigate. The freshly
      // loaded route reads the just-persisted Zustand session out of
      // localStorage. returnTo is the original deep-link target the SPA
      // passed into /auth/oidc/start; falls back to "/" when unset.
      window.location.replace(returnTo);

      return;
    }

    if (onboardingToken) {
      // Tab-scoped, short-lived. Cleared by usePersonify after the exchange
      // succeeds (or on any failure path).
      sessionStorage.setItem(ONBOARDING_TOKEN_STORAGE_KEY, onboardingToken);
      window.location.replace(returnTo);

      return;
    }

    sessionStorage.removeItem(ONBOARDING_TOKEN_STORAGE_KEY);
    void navigate({ to: "/login", search: { error: LOGIN_ERRORS.MISSING_TOKEN } });
  }, [navigate, queryError, setSession]);

  return (
    <TimedLoader shouldAutoComplete={false}>
      <Heading className="text-xl sm:text-3xl" level={1}>
        Signing you in…
      </Heading>
    </TimedLoader>
  );
};

export const Route = createFileRoute("/_guest/auth/oidc/return/")({
  component: OidcReturn,
  validateSearch: z.object({
    error: z.enum(LOGIN_ERRORS).optional().catch(undefined),
  }),
});
